import { History } from 'history';
import { Dispatch } from 'redux';
import { Institution } from '../../../../../../global/models/institution-model';
import { Person } from '../../../../../../global/models/person-model';
import { TcsmEpiApiState } from '../../../../../../root/tcsm-model';
import { PlanSummary } from '../../institution-model';
import { AdoptionStep, PlanAdoptionFormTypes } from '../models/plan-adoption-form-models';
import { PlanAdoptionRoutes } from '../routes';
import { LayDbPlanAdoptionWizard } from '../wizards/lay-db-plan-adoption-wizard';
import { LayDcAdoptionDetailsWizard } from '../wizards/lay-dc/lay-dc-adoption-details-wizard';
import { LayDcAmendDetailsWizard } from '../wizards/lay-dc/lay-dc-amend-details-wizard';
import { LayDcAmendIndemnifyWizard } from '../wizards/lay-dc/lay-dc-amend-indemnify-wizard';
import { LayDcAmendQuestionsWizard } from '../wizards/lay-dc/lay-dc-amend-question-wizard';
import { LayDcAmendReviewWizard } from '../wizards/lay-dc/lay-dc-amend-review-wizard';
import { LayDcIndemnifyWizard } from '../wizards/lay-dc/lay-dc-indemnify-wizard';
import { LayDcPlanAdoptionWizard } from '../wizards/lay-dc/lay-dc-plan-adoption-wizard';
import { LayDcQuestionsWizard } from '../wizards/lay-dc/lay-dc-questions-wizard';
import { LayDcReviewWizard } from '../wizards/lay-dc/lay-dc-review-wizard';
import { QccoPlanAdoptionOptions } from '../wizards/qcco-plan-adoption-wizard';
import { RsvpAdoptionDetailsWizard } from '../wizards/rsvp/rsvp-adoption-details-wizard';
import { RsvpAmendDetailsWizard } from '../wizards/rsvp/rsvp-amend-details-wizard';
import { RsvpAmendIndemnifyWizard } from '../wizards/rsvp/rsvp-amend-indemnify-wizard';
import { RsvpAmendQuestionsWizard } from '../wizards/rsvp/rsvp-amend-question-wizard';
import { RsvpAmendReviewWizard } from '../wizards/rsvp/rsvp-amend-review-wizard';
import { RsvpIndemnifyWizard } from '../wizards/rsvp/rsvp-indemnify-wizard';
import { RsvpPlanAdoptionWizard } from '../wizards/rsvp/rsvp-plan-adoption-wizard';
import { RsvpQuestionsWizard } from '../wizards/rsvp/rsvp-questions-wizard';
import { RsvpReviewWizard } from '../wizards/rsvp/rsvp-review-wizard';

export interface PlanAdoptionInitialization {
    formType: PlanAdoptionFormTypes,
    dispatch?: Dispatch<any>,
    history?: History<unknown>,
    epiContent?: TcsmEpiApiState,
    onSuccessUrl?: string,
    featureRootPath?: string,
    institutionRootPath?: string,
    location?: any,
    person?: Person,
    currentInstitution?: Institution,
    planSummary?: PlanSummary,
    authToken?: string;
    loginTime?: string;
}

export function getAdoptionStepFromLocation(location: any): AdoptionStep {
    if (location?.pathname?.includes(PlanAdoptionRoutes.QccoQuestions)) {
        return AdoptionStep.QUESTIONS;
    }

    if (location?.pathname?.includes(PlanAdoptionRoutes.Review)) {
        return AdoptionStep.REVIEW;
    }

    if (location?.pathname?.includes(PlanAdoptionRoutes.AdoptionDetails)) {
        return AdoptionStep.DETAILS;
    }

    if (location?.pathname?.includes(PlanAdoptionRoutes.Indemnify)) {
        return AdoptionStep.INDEMNIFY;
    }

    return AdoptionStep.QUESTIONS;
}

function getLayDcAdoptionWizard(currentStep: AdoptionStep, options: QccoPlanAdoptionOptions) {
    switch (currentStep) {
        case AdoptionStep.QUESTIONS:
            return new LayDcQuestionsWizard(options);

        case AdoptionStep.DETAILS:
            return new LayDcAdoptionDetailsWizard(options);

        case AdoptionStep.REVIEW:
            return new LayDcReviewWizard(options);

        case AdoptionStep.INDEMNIFY:
            return new LayDcIndemnifyWizard(options);

        default:
            return new LayDcPlanAdoptionWizard(options);
    }
}

function getRsvpAdoptionWizard(currentStep: AdoptionStep, options: QccoPlanAdoptionOptions) {
    switch (currentStep) {
        case AdoptionStep.QUESTIONS:
            return new RsvpQuestionsWizard(options);

        case AdoptionStep.DETAILS:
            return new RsvpAdoptionDetailsWizard(options);

        case AdoptionStep.REVIEW:
            return new RsvpReviewWizard(options);

        case AdoptionStep.INDEMNIFY:
            return new RsvpIndemnifyWizard(options);

        default:
            return new RsvpPlanAdoptionWizard(options);
    }
}

function getRsvpAmendmentWizard(currentStep: AdoptionStep, options: QccoPlanAdoptionOptions) {
    switch (currentStep) {
        case AdoptionStep.QUESTIONS:
            return new RsvpAmendQuestionsWizard(options);

        case AdoptionStep.DETAILS:
            return new RsvpAmendDetailsWizard(options);

        case AdoptionStep.REVIEW:
            return new RsvpAmendReviewWizard(options);

        case AdoptionStep.INDEMNIFY:
            return new RsvpAmendIndemnifyWizard(options);

        default:
            return null;
    }
}

function getLayDcAmendmentWizard(currentStep: AdoptionStep, options: QccoPlanAdoptionOptions) {
    switch (currentStep) {
        case AdoptionStep.QUESTIONS:
            return new LayDcAmendQuestionsWizard(options);

        case AdoptionStep.DETAILS:
            return new LayDcAmendDetailsWizard(options);

        case AdoptionStep.REVIEW:
            return new LayDcAmendReviewWizard(options);

        case AdoptionStep.INDEMNIFY:
            return new LayDcAmendIndemnifyWizard(options);

        default:
            return null;
    }
}

export function GetQccoAmendmentWizard(initialization: PlanAdoptionInitialization, dcPlanDetail: any, rsvpPlanDetail: any) {
    const currentStep = getAdoptionStepFromLocation(initialization?.location);
    const options: QccoPlanAdoptionOptions = {
        dispatch: initialization?.dispatch,
        history: initialization?.history,
        epiContent: initialization?.epiContent,
        featureRootPath: initialization?.featureRootPath,
        institutionRootPath: initialization?.institutionRootPath,
        location: initialization?.location,
        formType: initialization?.formType,
        dcPlanDetail,
        rsvpPlanDetail,
        person: initialization?.person,
        currentInstitution: initialization?.currentInstitution,
        planSummary: initialization?.planSummary,
        authToken: initialization?.authToken,
        loginTime: initialization?.loginTime,
    };

    if (!initialization?.formType) return null;

    switch (initialization?.formType) {

        case PlanAdoptionFormTypes.LayDc:
            return getLayDcAmendmentWizard(currentStep, options);

        case PlanAdoptionFormTypes.Rsvp:
            return getRsvpAmendmentWizard(currentStep, options);

        case PlanAdoptionFormTypes.ClergyDb:
        case PlanAdoptionFormTypes.LayDb:
        default:
            return null;
    }
}

export function GetPlanAdoptionWizard(initialization: PlanAdoptionInitialization) {
    const currentStep = getAdoptionStepFromLocation(initialization?.location);
    const options: QccoPlanAdoptionOptions = {
        dispatch: initialization?.dispatch,
        history: initialization?.history,
        epiContent: initialization?.epiContent,
        featureRootPath: initialization?.featureRootPath,
        institutionRootPath: initialization?.institutionRootPath,
        location: initialization?.location,
        formType: initialization?.formType,
        person: initialization?.person,
        currentInstitution: initialization?.currentInstitution,
        planSummary: initialization?.planSummary,
        authToken: initialization?.authToken,
        loginTime: initialization?.loginTime,
    };

    if (!initialization?.formType) return null;

    switch (initialization?.formType) {
        case PlanAdoptionFormTypes.ClergyDb:
            return null;

        case PlanAdoptionFormTypes.LayDb:
            return new LayDbPlanAdoptionWizard(initialization?.dispatch, initialization?.history, initialization?.epiContent, initialization?.onSuccessUrl);

        case PlanAdoptionFormTypes.LayDc:
            return getLayDcAdoptionWizard(currentStep, options);

        case PlanAdoptionFormTypes.Rsvp:
            return getRsvpAdoptionWizard(currentStep, options);

        default:
            return null;
    }
}